/**
 * Criteria CRUD drawer — create, edit, delete user-owned review criteria.
 * Built as a Dialog (we don't have a Sheet primitive yet; spec permits
 * Dialog as a v1 equivalent surface).
 */

import { useState } from 'react'
import { Trash2, Plus, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useReviewStore } from '@/stores/review-store'
import type { ReviewCriterion } from '@/lib/schemas'

interface CriteriaDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CriteriaDrawer({ open, onOpenChange }: CriteriaDrawerProps) {
  const criteria = useReviewStore((s) => s.criteria)
  const [editing, setEditing] = useState<ReviewCriterion | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const mine = criteria?.mine ?? []

  const closeAll = () => {
    setEditing(null)
    setIsCreating(false)
  }

  const handleClose = (next: boolean) => {
    if (!next) closeAll()
    onOpenChange(next)
  }

  const showForm = isCreating || editing !== null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isCreating ? 'New criterion' : editing ? 'Edit criterion' : 'My review criteria'}
          </DialogTitle>
          <DialogDescription>
            {isCreating || editing
              ? 'Criteria you create are private to your account.'
              : 'Create and manage custom review criteria for your account.'}
          </DialogDescription>
        </DialogHeader>

        {showForm ? (
          <CriterionForm
            initial={editing}
            onCancel={closeAll}
            onSaved={closeAll}
          />
        ) : (
          <MyCriteriaList
            items={mine}
            onEdit={setEditing}
            onNew={() => setIsCreating(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

// ===================
// List view
// ===================

function MyCriteriaList({
  items,
  onEdit,
  onNew,
}: {
  items: ReviewCriterion[]
  onEdit: (c: ReviewCriterion) => void
  onNew: () => void
}) {
  const deleteCriterion = useReviewStore((s) => s.deleteCriterion)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await deleteCriterion(id)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          You haven't created any criteria yet.
        </p>
      ) : (
        <ul className="divide-y border rounded-md">
          {items.map((criterion) => (
            <li key={criterion.id} className="px-3 py-2 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{criterion.name}</div>
                {criterion.description && (
                  <div className="text-meta line-clamp-2">
                    {criterion.description}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onEdit(criterion)}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:text-destructive"
                  onClick={() => void handleDelete(criterion.id)}
                  disabled={deletingId === criterion.id}
                  aria-label={`Delete ${criterion.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <DialogFooter>
        <Button size="sm" onClick={onNew} className="gap-1">
          <Plus className="h-3.5 w-3.5" />
          New criterion
        </Button>
      </DialogFooter>
    </div>
  )
}

// ===================
// Create / edit form
// ===================

function CriterionForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial: ReviewCriterion | null
  onCancel: () => void
  onSaved: () => void
}) {
  const createCriterion = useReviewStore((s) => s.createCriterion)
  const updateCriterion = useReviewStore((s) => s.updateCriterion)

  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [prompt, setPrompt] = useState(initial?.prompt ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isValid = name.trim().length > 0 && prompt.trim().length > 0 && prompt.trim().length <= 2000

  const handleSubmit = async () => {
    if (!isValid) return
    setSaving(true)
    setError(null)
    try {
      if (initial) {
        await updateCriterion(initial.id, {
          name: name.trim(),
          description: description.trim() || null,
          prompt: prompt.trim(),
        })
      } else {
        await createCriterion({
          name: name.trim(),
          description: description.trim() || null,
          prompt: prompt.trim(),
        })
      }
      onSaved()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-md px-2 py-1.5">
          <X className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 min-w-0">{error}</span>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="criterion-name" className="text-xs">Name</Label>
        <Input
          id="criterion-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Citation coverage"
          maxLength={120}
          disabled={saving}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="criterion-description" className="text-xs">
          Description <span className="text-muted-foreground">(optional)</span>
        </Label>
        <Input
          id="criterion-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Shown in the criteria picker as a tooltip"
          maxLength={500}
          disabled={saving}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="criterion-prompt" className="text-xs">Prompt</Label>
        <Textarea
          id="criterion-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Tell the reviewer what to look for. Be specific about what should be flagged and what should not."
          className="min-h-[150px] text-xs font-mono"
          maxLength={2000}
          disabled={saving}
        />
        <p className="text-[11px] text-muted-foreground">
          {prompt.length} / 2000 characters
        </p>
      </div>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void handleSubmit()} disabled={!isValid || saving}>
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Create criterion'}
        </Button>
      </DialogFooter>
    </div>
  )
}
